import { useEffect, useRef, useState } from 'react';
import beTwMerge from '@/utils/beTwMerge';
import {
  getAnchorId,
  getComponentViewportMargins,
  getCtaMaxWidth,
  isRichTextEmpty,
  resetCtaWidth,
  setCtaSameWidth,
} from '@/utils/builderUtils';
import ResponsiveImage, { ResponsiveImageSet } from '@/utils/ResponsiveImage';
import { getDataAttributes } from '@/utils/dataAttributes';
import { ActionWithGAEvents } from '@/models/GlobalComponentProps';
import { trackEventWithGaEvents } from '@/utils/gaDataPush';
import { GaEventsProps } from '@/utils/gaTypes';
import Button from '@/components/homepage/Button/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import { moreDescriptiveAriaLabel } from '@/utils/utils';
import RichText from '@/components/RichText/RichText';

interface IGatewayCopyBlock {
  alignment?: string;
  background: string;
  optionalEyebrowHeading?: string;
  optionalLargeHeader?: string;
  description?: string;
  mediaLeft: boolean;
  isVideo: boolean;
  videoThumbnail?: string;
  videoSrc?: string;
  loop: boolean;
  controls: boolean;
  autoPlay: boolean;
  imgSrc?: string;
  MobileImageSrc?: string;
  imgAlt?: string;
  imgLink?: string;
  imgGaEvents?: GaEventsProps[];
  actions?: ActionWithGAEvents[];
  isOneThird?: boolean;
  isFullBleed?: boolean;
  isSetMaxWidth?: boolean;
  margin?: boolean;
  mobileMargin?: boolean;
  anchorLinkID?: string;
}

export default function GatewayCopyBlock(props: IGatewayCopyBlock) {
  const buttonRefs = useRef<any[]>([]);
  const [calculatedContainerWidth, setCalculatedContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (buttonRefs.current.length < 2) return;
    const resizeWidth = () => {
      const resetWidth = window.innerWidth < 1024 ? '100%' : 'auto';
      resetCtaWidth(buttonRefs, resetWidth);
      if (window.innerWidth < 1024) return;
      const maxWidth = getCtaMaxWidth(buttonRefs);
      const containerElement = document.getElementById('gateway-copy-block-text-container');
      let containerContentWidth = 0;
      if (containerElement) {
        const { clientWidth } = containerElement;
        const style = window.getComputedStyle(containerElement);
        const paddingLeft = parseFloat(style.paddingLeft);
        const paddingRight = parseFloat(style.paddingRight);
        containerContentWidth = clientWidth - paddingLeft - paddingRight;
      }
      const optimalContainerWidth = Math.min(maxWidth * 2 + 18, containerContentWidth);
      setCalculatedContainerWidth(optimalContainerWidth);
      setCtaSameWidth(buttonRefs, maxWidth);
    };
    resizeWidth();
    window.addEventListener('resize', resizeWidth);
    return () => {
      window.removeEventListener('resize', resizeWidth);
    };
  }, [buttonRefs]);

  const actions = props?.actions?.map(action => ({
    ...action,
    // @ts-ignore
    color: action.color === 'dark' ? 'dark-green' : 'white',
  }));
  const isTextBoxCenter = props.alignment === 'center';
  const backgroundColor = {
    'dark-green': 'tw-bg-color-dark-green tw-text-white',
    'light-green': 'tw-bg-color-light-green tw-text-white',
    'light-mauve': 'tw-bg-color-light-mauve',
    cream: 'tw-bg-color-cream',
    white: 'tw-bg-white',
  };
  const formattedBackground = props.background.replace(' ', '-').toLowerCase();

  const imageSrcs: ResponsiveImageSet = [
    {
      src: props.MobileImageSrc || '',
      size: { width: 735, height: 556, aspectRatio: 169 / 128 },
      breakpoint: 1023,
    },
    {
      src: props.imgSrc || '',
      size: { width: 720, height: 545, aspectRatio: 169 / 128 },
      breakpoint: 1024,
    },
  ];

  const getVideo = () => {
    if (props.isVideo && props.videoSrc) {
      if (props.imgLink) {
        return (
          <video
            className="tw-aspect-[169/128] tw-w-full tw-object-cover tw-cursor-pointer"
            autoPlay={props.autoPlay}
            muted
            loop={props.loop}
            controls={props.controls}
            playsInline
            aria-label={props.optionalLargeHeader || ''}
            poster={props.videoThumbnail || ''}
            onClick={() => {
              window.location.href = props.imgLink || '';
              props.imgGaEvents?.forEach(gaEvent => trackEventWithGaEvents(gaEvent, false));
            }}
          >
            <source src={props.videoSrc} type="video/mp4" />
          </video>
        );
      }

      return (
        <video
          className="tw-aspect-[169/128] tw-w-full tw-object-cover"
          autoPlay={props.autoPlay}
          muted
          aria-label={props.optionalLargeHeader || ''}
          loop={props.loop}
          controls={props.controls}
          playsInline
          poster={props.videoThumbnail || ''}
        >
          <source src={props.videoSrc} type="video/mp4" />
        </video>
      );
    }
  };

  return (
    <div
      className={beTwMerge(
        !props.isFullBleed && 'tw-py-8 tw-mx-4 lg:tw-mx-8',
        props.isSetMaxWidth && 'tw-max-w-[1440px] min-[1504px]:tw-mx-auto',
        getComponentViewportMargins(props.margin, props.mobileMargin)
      )}
      {...getAnchorId(props.anchorLinkID)}
    >
      <div className="tw-grid tw-col-span-full lg:tw-grid-cols-12 tw-w-full">
        {/* Text side */}
        <div
          className={beTwMerge(
            'large:tw-px-20 tw-px-4 tw-pt-6 tw-pb-14 desktop:tw-pt-8 lg:tw-py-0 tw-col-span-full tw-flex tw-flex-col tw-justify-center lg:tw-col-start-7 lg:tw-col-span-6',
            props.isOneThird && 'md:tw-px-12 lg:tw-col-start-9 lg:tw-col-span-4',
            !props.mediaLeft && 'lg:tw-col-start-1',
            backgroundColor[formattedBackground],
            isTextBoxCenter && 'tw-items-center tw-text-center'
          )}
          id="gateway-copy-block-text-container"
        >
          {props.optionalEyebrowHeading?.trim() && (
            <h4 className="tw-mt-0 tw-text-[13px] tw-font-roman tw-leading-[17.76px] tw-tracking-[0.65px] tw-mb-1 large:tw-mb-2">
              {props.optionalEyebrowHeading.trim()}
            </h4>
          )}
          {props.optionalLargeHeader && <h2 className="tw-mb-4 tw-mt-0 large:tw-mb-6">{props.optionalLargeHeader}</h2>}
          {!isRichTextEmpty(props.description) && (
            <RichText
              className={props.actions ? 'tw-mb-8 [&_p]:tw-mb-4' : 'tw-mb-0 [&_p]:tw-mb-4'}
              content={props.description!}
              deep={2}
            />
          )}

          {actions && (
            <div
              className={beTwMerge(
                'tw-flex tw-font-avenirmedium tw-flex-col tw-flex-wrap large:tw-flex-row tw-gap-2 md:tw-gap-4 tw-w-full',
                isTextBoxCenter && 'tw-justify-center'
              )}
              style={
                actions.length === 4 && calculatedContainerWidth
                  ? { width: `${calculatedContainerWidth}px` }
                  : undefined
              }
            >
              {actions.slice(0, 4).map((action, i) => {
                const { label, color, type } = action;
                if (action.actions) {
                  const { actions: subActions } = action;
                  return (
                    <DropdownButton
                      key={i}
                      label={label}
                      options={subActions}
                      // @ts-ignore
                      color={color}
                      type={type}
                      className={beTwMerge(
                        'tw-w-full tw-text-center tw-max-w-none [&>button]:tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis'
                      )}
                      onToggle={() => {
                        action?.gaEvents?.forEach(gaEvent => {
                          trackEventWithGaEvents(gaEvent, false);
                        });
                      }}
                      btnRef={(el: any) => {
                        buttonRefs.current[i] = el;
                      }}
                    />
                  );
                }
                const { onClick, href, gaEvents } = action;

                return (
                  <Button
                    className={beTwMerge(
                      'tw-max-w-none tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden'
                    )}
                    key={i}
                    label={label}
                    ariaLabel={moreDescriptiveAriaLabel(props.optionalLargeHeader, label || '')}
                    // @ts-ignore
                    color={color}
                    type={type}
                    href={href}
                    {...getDataAttributes({ gaEvents })}
                    onClick={() => {
                      gaEvents?.forEach(gaEvent => trackEventWithGaEvents(gaEvent, false));
                      onClick;
                    }}
                    ref={(el: any) => {
                      buttonRefs.current[i] = el;
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Media side */}
        <div
          className={beTwMerge(
            '-col-span-full tw-row-start-1 tw-w-full tw-h-full [&>picture]:tw-block [&>picture]:tw-w-full lg:tw-col-start-7 lg:tw-col-span-6',
            !props.isOneThird && 'tw-aspect-[169/128]',
            props.isOneThird && 'lg:tw-col-start-5 lg:tw-col-span-8',
            props.mediaLeft && 'lg:tw-col-start-1'
          )}
        >
          {!props.isVideo &&
            (props.imgSrc || props.MobileImageSrc) &&
            (props.imgLink ? (
              <a
                href={props.imgLink}
                className={`tw-block tw-w-full ${props.isOneThird ? '' : 'tw-aspect-[169/128]'} tw-object-cover`}
                onClick={() => {
                  props.imgGaEvents?.forEach(gaEvent => trackEventWithGaEvents(gaEvent, false));
                }}
              >
                <ResponsiveImage
                  className={`tw-w-full tw-h-full ${props.isOneThird ? '' : 'tw-aspect-[169/128]'} tw-object-cover`}
                  sources={imageSrcs}
                  alt={props.imgAlt || props.optionalLargeHeader}
                />
              </a>
            ) : (
              <ResponsiveImage
                className={`tw-w-full tw-h-full ${props.isOneThird ? '' : 'tw-aspect-[169/128]'} tw-object-cover`}
                sources={imageSrcs}
                alt={props.imgAlt || props.optionalLargeHeader}
              />
            ))}
          {getVideo()}
        </div>
      </div>
    </div>
  );
}
